import React, { Fragment, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useQuery } from "@tanstack/react-query";

import AdministrationHeader from "../../../components/header-components/AdministrationHeader.jsx";
import TableCard from "../../../components/ui-components/TableCard";
import FlexTable from "../../../components/ui-components/FlexTable";
import EmptyState from "../../../components/ui-components/EmptyState";
import Badge from "../../../components/ui-components/Badge";
import { listBudgetPolicies } from "../api";

const APPLIES_OPTIONS = [
  { value: "all", label: "All" },
  { value: "general", label: "General" },
  { value: "ai_use_case", label: "AI use case" },
];

const APPLIES_LABELS = { general: "General", ai_use_case: "AI use case" };

function MetaChip({ children, className = "" }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-ctrl border border-border bg-surface-2 px-2 py-0.5 mt-1 text-xs text-text-2 ${className}`.trim()}
    >
      {children}
    </span>
  );
}

function thresholdLabel(p) {
  const parts = [];
  if (p.soft_pct != null) parts.push(`Soft ${p.soft_pct}%`);
  if (p.hard_pct != null) parts.push(`Hard ${p.hard_pct}%`);
  return parts.join(" · ") || "No thresholds";
}

function spikeLabel(p) {
  const hasMult = p.spike_multiplier != null;
  if (p.anomaly_enabled && hasMult) return `Spike anomaly + ${p.spike_multiplier}×`;
  if (p.anomaly_enabled) return "Spike anomaly";
  if (hasMult) return `Spike ${p.spike_multiplier}×`;
  return "Spike off";
}

function policyHaystack(p) {
  return [p.name, APPLIES_LABELS[p.applies_to] || p.applies_to, thresholdLabel(p), p.is_default ? "default" : ""]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

const editIcon = icon({ name: "edit", style: "light" });

const PolicyListPage = () => {
  const navigate = useNavigate();
  const [appliesFilter, setAppliesFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const { data: policies, isLoading, error, refetch } = useQuery(["budget-policies"], listBudgetPolicies);

  const byApplies = useMemo(() => {
    const all = policies || [];
    if (appliesFilter === "all") return all;
    return all.filter((p) => (p.applies_to || "general") === appliesFilter);
  }, [policies, appliesFilter]);

  const search = searchQuery.trim();
  const searchLower = search.toLowerCase();
  const filtered = useMemo(
    () => (searchLower ? byApplies.filter((p) => policyHaystack(p).includes(searchLower)) : byApplies),
    [byApplies, searchLower]
  );

  const matchCount = filtered.length;
  const noun = matchCount === 1 ? "policy" : "policies";
  const hint = isLoading
    ? "Loading…"
    : search
      ? `Showing ${matchCount} ${noun} matching "${search}"`
      : `Showing ${matchCount} ${noun}`;

  const columnDefs = useMemo(
    () => [
      {
        accessor: "name",
        Header: () => <span>Policy</span>,
        Cell: ({ row }) => {
          const p = row.original;
          return (
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-medium text-text truncate">{p.name || "Untitled"}</span>
                {p.is_default && (
                  <span className="shrink-0" title="Organization default policy">
                    <Badge status="info" label="Default" />
                  </span>
                )}
              </div>
              <div className="mt-1 flex min-w-0 items-center gap-1.5 overflow-hidden" title={thresholdLabel(p)}>
                <MetaChip className="shrink-0">{thresholdLabel(p)}</MetaChip>
                <MetaChip className="shrink-0">{spikeLabel(p)}</MetaChip>
                <MetaChip className="shrink-0">
                  {(p.steps?.length ?? 0)} step{(p.steps?.length ?? 0) === 1 ? "" : "s"}
                </MetaChip>
              </div>
            </div>
          );
        },
        flex: 1,
        minWidth: 260,
      },
      {
        accessor: "applies_to",
        Header: () => <span>Applies to</span>,
        Cell: ({ value }) => <span className="text-text-2 text-sm">{APPLIES_LABELS[value] || "General"}</span>,
        width: 150,
        minWidth: 120,
        maxWidth: 190,
      },
      {
        accessor: "breach_eta_days",
        id: "eta",
        align: "right",
        Header: () => <span>Predictive ETA</span>,
        Cell: ({ value }) =>
          typeof value === "number" ? (
            <span className="tabular-nums text-text-2">{`≤ ${value}d`}</span>
          ) : (
            <span className="text-text-2">—</span>
          ),
        width: 130,
        minWidth: 110,
        maxWidth: 160,
      },
      {
        accessor: "budgets_governed",
        id: "budgets",
        align: "right",
        Header: () => <span>Budgets</span>,
        Cell: ({ value }) => <span className="tabular-nums text-text-2">{value ?? 0}</span>,
        width: 110,
        minWidth: 90,
        maxWidth: 140,
      },
      {
        id: "actions",
        align: "center",
        Header: () => null,
        Cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/budget-enforcement/policies/${row.original.id}`);
              }}
              className="text-lg px-2 text-text-2 hover:text-text hover:bg-surface-2 rounded-ctrl transition-colors cursor-pointer"
              title="Edit policy"
            >
              <FontAwesomeIcon icon={editIcon} className="w-4 h-4" />
            </button>
          </div>
        ),
        width: 90,
        minWidth: 80,
        maxWidth: 110,
      },
    ],
    [navigate]
  );

  return (
    <Fragment>
      <AdministrationHeader title="Budget Policies" showPeriodSelector={false} />

      <div className="w-full p-8">
        <TableCard
          title="Budget policies"
          subtitle={hint}
          primaryAction={{
            label: "New Policy",
            icon: icon({ name: "plus" }),
            onClick: () => navigate("/budget-enforcement/policies/new"),
          }}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search policies…"
          filterValue={appliesFilter}
          onFilterChange={setAppliesFilter}
          filterOptions={APPLIES_OPTIONS}
          filterAriaLabel="Filter by applies-to"
          isLoading={isLoading}
          error={error}
          errorMessage="Error loading policies"
          onRetry={refetch}
        >
          {(policies || []).length === 0 ? (
            <EmptyState
              icon={icon({ name: "shield-check", family: "sharp", style: "light" })}
              title="No budget policies yet"
              message="Create a policy to define spend thresholds and escalation steps."
              action={{
                label: "Create policy",
                onClick: () => navigate("/budget-enforcement/policies/new"),
                icon: icon({ name: "plus", style: "regular" }),
              }}
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={icon({ name: "magnifying-glass" })}
              title="No matching policies"
              message={search ? `No policies match "${search}".` : "No policies for this filter."}
              action={{
                label: "Clear search",
                onClick: () => setSearchQuery(""),
                icon: icon({ name: "xmark", style: "regular" }),
                variant: "secondary",
              }}
            />
          ) : (
            <FlexTable
              columns={columnDefs}
              data={filtered}
              getRowId={(p) => p.id}
              onRowClick={(id) => navigate(`/budget-enforcement/policies/${id}`)}
              rowAriaLabel={(p) => `Edit policy ${p.name}`}
            />
          )}
        </TableCard>
      </div>
    </Fragment>
  );
};

export default PolicyListPage;
